/**
 * 뮤직비디오 관련 상수
 */

// 뮤직비디오 상태
export const MUSIC_VIDEO_STATUS = {
  PENDING: 'pending',
  SCENE_GENERATING: 'scene_generating',
  SCENES_READY: 'scenes_ready',
  CLIP_GENERATING: 'clip_generating',
  CLIPS_READY: 'clips_ready',
  COMPOSITING: 'compositing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type MusicVideoStatusType = (typeof MUSIC_VIDEO_STATUS)[keyof typeof MUSIC_VIDEO_STATUS];

// 상태별 표시 정보
export const MUSIC_VIDEO_STATUS_INFO: Record<MusicVideoStatusType, {
  label: string;
  color: string;
  bgColor: string;
  description: string;
}> = {
  [MUSIC_VIDEO_STATUS.PENDING]: {
    label: '대기',
    color: 'text-neutral-600',
    bgColor: 'bg-neutral-100',
    description: '뮤직비디오가 생성 대기 중입니다.',
  },
  [MUSIC_VIDEO_STATUS.SCENE_GENERATING]: {
    label: '씬 생성 중',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Claude AI가 씬 프롬프트를 생성하고 있습니다.',
  },
  [MUSIC_VIDEO_STATUS.SCENES_READY]: {
    label: '씬 준비 완료',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    description: '씬 프롬프트 생성이 완료되었습니다. 수정 후 클립 생성을 시작하세요.',
  },
  [MUSIC_VIDEO_STATUS.CLIP_GENERATING]: {
    label: '클립 생성 중',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Kling AI가 비디오 클립을 생성하고 있습니다.',
  },
  [MUSIC_VIDEO_STATUS.CLIPS_READY]: {
    label: '클립 준비 완료',
    color: 'text-violet-600',
    bgColor: 'bg-violet-100',
    description: '클립 생성이 완료되었습니다. 클립을 선택하고 합성을 시작하세요.',
  },
  [MUSIC_VIDEO_STATUS.COMPOSITING]: {
    label: '합성 중',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    description: '선택된 클립들을 합성하고 있습니다.',
  },
  [MUSIC_VIDEO_STATUS.COMPLETED]: {
    label: '완료',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    description: '뮤직비디오가 완성되었습니다.',
  },
  [MUSIC_VIDEO_STATUS.FAILED]: {
    label: '실패',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    description: '뮤직비디오 생성 중 오류가 발생했습니다.',
  },
};

// 비동기 작업 진행 중인 상태 (폴링 필요)
export const MUSIC_VIDEO_POLLING_STATUSES: MusicVideoStatusType[] = [
  MUSIC_VIDEO_STATUS.SCENE_GENERATING,
  MUSIC_VIDEO_STATUS.CLIP_GENERATING,
  MUSIC_VIDEO_STATUS.COMPOSITING,
];

// 클립 상태
export const CLIP_STATUS = {
  PENDING: 'pending',
  SUBMITTED: 'submitted',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

// 스타일 프리셋
export const STYLE_PRESETS = [
  { value: 'cinematic', label: '시네마틱' },
  { value: 'anime', label: '애니메이션' },
  { value: 'abstract', label: '추상적' },
  { value: 'realistic', label: '사실적' },
  { value: 'vintage', label: '빈티지' },
  { value: 'neon', label: '네온' },
] as const;

// 종횡비
export const ASPECT_RATIOS = [
  { value: '16:9', label: '가로 (16:9)' },
  { value: '9:16', label: '세로 (9:16)' },
  { value: '1:1', label: '정사각형 (1:1)' },
] as const;

// Kling 모드
export const KLING_MODES = [
  { value: 'std', label: 'Standard (빠른 생성)' },
  { value: 'pro', label: 'Professional (고품질)' },
] as const;

// 카메라 무브먼트
export const CAMERA_MOVEMENTS = [
  { value: 'static', label: '정적' },
  { value: 'slow_zoom_in', label: '느린 줌인' },
  { value: 'slow_zoom_out', label: '느린 줌아웃' },
  { value: 'slow_pan_left', label: '좌측 패닝' },
  { value: 'slow_pan_right', label: '우측 패닝' },
  { value: 'slow_pan_up', label: '상향 패닝' },
  { value: 'slow_pan_down', label: '하향 패닝' },
  { value: 'tracking_shot', label: '트래킹 샷' },
  { value: 'orbit', label: '궤도 회전' },
  { value: 'dolly_in', label: '돌리 인' },
  { value: 'dolly_out', label: '돌리 아웃' },
] as const;

// 샷 타입
export const SHOT_TYPES = [
  { value: 'wide', label: '와이드' },
  { value: 'extreme_wide', label: '익스트림 와이드' },
  { value: 'medium', label: '미디엄' },
  { value: 'close_up', label: '클로즈업' },
  { value: 'extreme_close_up', label: '익스트림 클로즈업' },
  { value: 'aerial', label: '에어리얼' },
  { value: 'low_angle', label: '로우앵글' },
  { value: 'high_angle', label: '하이앵글' },
  { value: 'over_the_shoulder', label: '오버더숄더' },
] as const;

// 전환 효과
export const TRANSITION_TYPES = [
  { value: 'cut', label: '컷' },
  { value: 'crossfade', label: '크로스페이드' },
  { value: 'fade_to_black', label: '페이드 투 블랙' },
  { value: 'dissolve', label: '디졸브' },
] as const;

// 클립 길이
export const CLIP_DURATIONS = [
  { value: '5', label: '5초 (빠른 템포)' },
  { value: '10', label: '10초 (감성적/클라이맥스)' },
] as const;
